import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/_atoms/select";
import { cn } from "@/lib/cn";
import { ArrowDownIcon, ArrowUpIcon } from "@radix-ui/react-icons";

import { DatatableRowData } from "../datatable-types";
import { buildColumnDisplayValueComparator } from "../datatable-utils/build-column-display-value-comparator";
import { useDatatableContext } from "../use-datatable-context";

import { useColumnDisplayValue } from "./use-column-display-value";

export function DatatableDisplayOptionsOrderingSelect<TData extends DatatableRowData>() {
  const { table, lock } = useDatatableContext<TData>();
  const { t } = useTranslation("common");
  const getDisplayValueForColumn = useColumnDisplayValue<TData>();

  const sortableColumnList = useMemo(() => {
    return table.getAllColumns().filter((column) => column.getCanSort());
  }, [table]);

  const selectableColumnList = useMemo(() => {
    return sortableColumnList
      .filter((column) => lock.canOfferColumn(column))
      .sort(buildColumnDisplayValueComparator(getDisplayValueForColumn));
  }, [sortableColumnList, lock, getDisplayValueForColumn]);

  const sortedColumn = sortableColumnList.find((column) => column.getIsSorted() !== false);
  const isDescending = sortedColumn?.getIsSorted() === "desc";

  const handleValueChange = (columnId: string) => {
    const column = sortableColumnList.find((sortableColumn) => sortableColumn.id === columnId);

    if (!column) {
      return;
    }

    const descending = column.getIsSorted() === "asc";

    table.resetSorting();
    column.toggleSorting(descending);
  };

  const handleDirectionToggle = () => {
    sortedColumn?.toggleSorting(!isDescending);
  };

  return (
    <div className="flex w-full flex-row items-center gap-1">
      <Select value={sortedColumn?.id ?? ""} onValueChange={handleValueChange}>
        <SelectTrigger className="h-6 px-2 text-xs ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus">
          <div className="flex flex-row items-center justify-between text-xs">
            {!sortedColumn && t("view.clickToSort")}
            {sortedColumn && (
              <>
                {isDescending ? <ArrowDownIcon className="mr-2 h-3 w-3" /> : <ArrowUpIcon className="mr-2 h-3 w-3" />}
                {getDisplayValueForColumn(sortedColumn.id)}
              </>
            )}
          </div>
        </SelectTrigger>
        <SelectContent className="z-10000 p-0">
          {selectableColumnList.map((column) => {
            return (
              <SelectItem
                key={column.id}
                value={column.id}
                className="flex h-6 flex-row items-center justify-between text-xs"
              >
                {getDisplayValueForColumn(column.id)}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {sortedColumn && (
        <Button
          variant={"ghost"}
          size="icon"
          className={cn(["ml-1 h-6 rounded-md border text-left text-xs font-normal "])}
          aria-label={t("view.toggleSortDirection")}
          onClick={handleDirectionToggle}
        >
          {isDescending ? <ArrowDownIcon className="mx-2 h-3 w-3" /> : <ArrowUpIcon className="mx-2 h-3 w-3" />}
        </Button>
      )}
    </div>
  );
}
